import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { onSnapshot, collection, query, where, updateDoc, doc, orderBy, limit, getDocs } from "firebase/firestore";
import { db, auth } from "../../firebase";
import "./usercss/appointments.css";
import MyAppointments from "./MyAppointmentsPanel";
import CancelAppointmentModal from "../../components/usercomponents/CancelAppointmentModal";
import { appointmentService } from "../../services/appointmentService";
import { userService } from "../../services/firestoreService";
import { useLanguage } from "../../contexts/LanguageContext";
import { translations } from "../../translations";
import { getNextBusinessDay } from "../../utils/dateValidation";
import { getSlotValidation } from "../../utils/appointmentUtils";

// ----- Helper Functions -----
const BOOK_STEPS = ["location", "reason", "datetime", "patient"];
const DEFAULT_CLINIC_ID = "park-ave";
const DEFAULT_MEDICAL_PROFESSIONAL = {
  uid: "pending",
  fullName: "To be assigned"
};
const ACTIVE_SLOT_STATUSES = [
  "PENDING",
  "CONFIRMED",
  "CANCELLING"
];
const SERVICE_SLOT_RULES = {
  sti_testing: { interval: 20, close: 17 * 60 },
  family_planning: { interval: 20, close: 14 * 60 + 40 },
  rh_consult: { interval: 30, close: 17 * 60 }
};
const CLINICS = [
  {
    id: "park-ave",
    name: "Park Avenue Clinic Branch",
    description: "2442 Park Ave, Pasay City, Metro Manila - Offers reproductive, STI, and general health services.",
    address: "2442 Park Ave, Pasay City, Metro Manila",
    phone: "(02) 8123 4567",
    hours: "Monday - Friday 8:00 AM to 5:00 PM"
  }
];
const VISIT_REASONS = [
  { id: "rh_consult", title: "Reproductive Health Consultation", description: "General reproductive health advice and check-up", hours: "8 AM to 5 PM" },
  { id: "family_planning", title: "Family Planning / Contraceptive Services", description: "Counseling and provision of contraceptive methods", hours: "8 AM to 2:20 PM" },
  { id: "sti_testing", title: "STI Testing / Screening", description: "Confidential STI screening and counseling", hours: "8 AM to 5 PM" }
];

function NotificationModal({ message, type, onClose }) {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, onClose]);

  if (!message) return null;

  return (
    <div
      className={`appointments-feedback-modal ${type}`}
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        padding: "12px 20px",
        borderRadius: "8px",
        color: "#fff",
        backgroundColor: type === "success" ? "#36C667" : "#e74c3c",
        boxShadow: "0 4px 6px rgba(0,0,0,0.2)",
        zIndex: 9999,
        transition: "opacity 0.3s",
      }}
    >
      {message}
    </div>
  );
}

function generateReference() {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Manila',
    year: '2-digit',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(now).reduce((acc, part) => {
    if (part.type === 'year') acc.year = part.value;
    if (part.type === 'month') acc.month = part.value;
    if (part.type === 'day') acc.day = part.value;
    return acc;
  }, { year: '', month: '', day: '' });

  const randomDigits = () => String(Math.floor(Math.random() * 10000)).padStart(4, '0');
  return `AS-${parts.year}${parts.month}${parts.day}-${randomDigits()}`;
}

// Helper to get YYYY-MM-DD in Asia/Manila timezone
function formatDateToPH(date) {
  return new Intl.DateTimeFormat('en-CA', { // en-CA gives YYYY-MM-DD
    timeZone: 'Asia/Manila',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
}

// Helper to get current PH Time components
function getPHNow() {
  const now = new Date();
  const phTimeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Manila',
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric',
    hour12: false
  });
  
  const parts = phTimeFormatter.formatToParts(now);
  return {
    year: parseInt(parts.find(p => p.type === 'year').value),
    month: parseInt(parts.find(p => p.type === 'month').value) - 1,
    day: parseInt(parts.find(p => p.type === 'day').value),
    hour: parseInt(parts.find(p => p.type === 'hour').value),
    minute: parseInt(parts.find(p => p.type === 'minute').value)
  };
}

// Alias maps for normalizing selection lists
const OTHER_ALIAS = {
  "other": "Others (Please Specify)",
  "other (please specify)": "Others (Please Specify)",
  "other (Please specify)": "Others (Please Specify)",
  "others": "Others (Please Specify)",
  "others (please specify)": "Others (Please Specify)",
  "others (Please specify)": "Others (Please Specify)",
  "Others": "Others (Please Specify)",
  "Others (please specify)": "Others (Please Specify)",
  "Others (Please specify)": "Others (Please Specify)"
};

const STI_ALIASES = {
  chlamydia: "Chlamydia",
  chylamydia: "Chlamydia",
  "other": "Others (Please Specify)",
  "other (please specify)": "Others (Please Specify)",
  "others (please specify)": "Others (Please Specify)",
  "Other": "Others (Please Specify)",
  "Others (please specify)": "Others (Please Specify)"
};

const SURGERY_ALIASES = {
  "ceasrean": "Cesarean Section",
  "ceasrean section": "Cesarean Section",
  "cesarean": "Cesarean Section",
  "cesarean section": "Cesarean Section",
  "other": "Others (Please Specify)",
  "other (please specify)": "Others (Please Specify)",
  "others (please specify)": "Others (Please Specify)",
  "Other": "Others (Please Specify)",
  "Others (please specify)": "Others (Please Specify)"
};

const normalizeSelectionList = (list, aliasMap) => {
  const map = Object.keys(aliasMap).reduce((acc, key) => {
    acc[key.toLowerCase()] = aliasMap[key];
    return acc;
  }, {});
  return (Array.isArray(list) ? list : [])
    .map((item) => {
      const trimmed = String(item || "").trim();
      if (!trimmed) return "";
      return map[trimmed.toLowerCase()] || trimmed;
    })
    .filter(Boolean);
};

function getLast10Digits(value) {
  return String(value || "").replace(/\D/g, "").slice(-10);
}

// Helper to sanitize input - only allow a-z, ', -, spaces, and newlines
function sanitizeInput(value) {
  return value.replace(/[^a-zA-Z'\- \n]/g, '');
}

// Component for textarea with counter
function TextareaWithCounter({ value, onChange, placeholder, maxLength = 150, required = false }) {
  const handleChange = (e) => {
    const sanitized = sanitizeInput(e.target.value);
    const truncated = sanitized.slice(0, maxLength);
    onChange(truncated);
  };

  const hasError = required && !value.trim();

  return (
    <div style={{ position: 'relative' }}>
      <textarea
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '11px 14px 32px 14px',
          borderRadius: '12px',
          border: `2px solid ${hasError ? '#dc2626' : '#e2e8f0'}`,
          fontSize: '14.5px',
          minHeight: '90px',
          resize: 'vertical',
          outline: 'none',
          transition: 'border-color 0.18s ease'
        }}
        onFocus={(e) => e.target.style.borderColor = hasError ? '#dc2626' : '#3b82f6'}
        onBlur={(e) => e.target.style.borderColor = hasError ? '#dc2626' : '#e2e8f0'}
      />
      <div style={{
        position: 'absolute',
        bottom: '8px',
        right: '12px',
        fontSize: '12px',
        color: value.length >= maxLength ? '#dc2626' : '#64748b',
        fontWeight: '500'
      }}>
        {value.length}/{maxLength}
      </div>
    </div>
  );
}

// Component for input with counter
function InputWithCounter({ value, onChange, placeholder, maxLength = 150, required = false }) {
  const handleChange = (e) => {
    const sanitized = sanitizeInput(e.target.value);
    const truncated = sanitized.slice(0, maxLength);
    onChange(truncated);
  };

  const hasError = required && !value.trim();

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '11px 60px 11px 14px',
          borderRadius: '12px',
          border: `2px solid ${hasError ? '#dc2626' : '#e2e8f0'}`,
          fontSize: '14.5px',
          marginTop: '8px',
          outline: 'none',
          transition: 'border-color 0.18s ease'
        }}
        onFocus={(e) => e.target.style.borderColor = hasError ? '#dc2626' : '#3b82f6'}
        onBlur={(e) => e.target.style.borderColor = hasError ? '#dc2626' : '#e2e8f0'}
      />
      <div style={{
        position: 'absolute',
        bottom: '10px',
        right: '12px',
        fontSize: '12px',
        color: value.length >= maxLength ? '#dc2626' : '#64748b',
        fontWeight: '500'
      }}>
        {value.length}/{maxLength}
      </div>
    </div>
  );
}

function minutesToTimeLabel(totalMinutes) {
  const hours24 = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const period = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 || 12;
  return `${hours12}:${String(minutes).padStart(2, "0")} ${period}`;
}

function timeLabelToMinutes(time) {
  const [timeStr, period] = time.split(" ");
  let [hours, minutes] = timeStr.split(":").map(Number);
  if (period === "PM" && hours !== 12) hours += 12;
  if (period === "AM" && hours === 12) hours = 0;
  return hours * 60 + minutes;
}

function getPatientFullName(userData, user) {
  const firstName = (userData?.firstName || userData?.personalInformation?.firstName || "").trim();
  const lastName = (userData?.lastName || userData?.personalInformation?.lastName || "").trim();
  const nameFromRequiredParts = `${firstName} ${lastName}`.trim();

  return (
    nameFromRequiredParts ||
    userData?.fullName ||
    userData?.fullname ||
    userData?.patientName ||
    user?.displayName ||
    "User"
  );
}

export default function AppointmentsPanel() {
  const { language } = useLanguage();
  const t = translations[language];
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const [activeNav, setActiveNav] = useState(() => {
    const storedNav = localStorage.getItem('appointmentNav');
    if (storedNav) {
      localStorage.removeItem('appointmentNav');
      return storedNav;
    }
    return "My Appointments";
  });
  const [historyActiveTab, setHistoryActiveTab] = useState("upcoming");
  const [userSex, setUserSex] = useState("");

  // Booking flow state
  const [expandedSection, setExpandedSection] = useState("location");
  const [completedSections, setCompletedSections] = useState({ location: true });
  const [selectedClinic, setSelectedClinic] = useState(DEFAULT_CLINIC_ID);
  const [selectedReason, setSelectedReason] = useState("");
  const [selectedDate, setSelectedDate] = useState(getNextBusinessDay(new Date()));
  const [selectedTime, setSelectedTime] = useState("");
  const [bookedSlots, setBookedSlots] = useState([]);
  const [patientDetails, setPatientDetails] = useState({
    fullName: "",
    email: "",
    phone: "",
    dob: "",
    guardianFullName: "",
    guardianRelationship: "",
    guardianContact: "",
    guardianAddress: "",
    assistanceRequired: ""
  });
  const [showBookingSuccess, setShowBookingSuccess] = useState(false);
  const [newAppointment, setNewAppointment] = useState(null);
  const [isBooking, setIsBooking] = useState(false);
  const [notification, setNotification] = useState({ message: "", type: "" });

  // Cancellation modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [apptToCancel, setApptToCancel] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  // Data fetching state
  const [myAppointments, setMyAppointments] = useState([]);
  const [isLoadingAppts, setIsLoadingAppts] = useState(false);
  
  // Refs for mounted state to prevent memory leaks
  const apptsMountedRef = useRef(null);
  const slotsMountedRef = useRef(null);

  // Health Information Pill Options
  const MEDICAL_HISTORY_OPTIONS = ["Obesity", "Liver Disease", "Diabetes", "Hypertension", "Chronic Kidney Disease", "Heart Disease", "Others (Please Specify)"];
  // eslint-disable-next-line no-unused-vars
  const ALLERGY_OPTIONS = ["Penicillin / Antibiotics", "Hormonal Medications (Pills, Patches, Injections)", "Others (Please Specify)"];
  const LIFESTYLE_OPTIONS = [
    { id: "smoking", label: "Smoking Status", options: ["Regulary", "Occasionally", "Never"] },
    { id: "alcohol", label: "Alcohol Use", options: ["Regulary", "Occasionally", "Never"] }
  ];
  const STI_HISTORY_OPTIONS = ["Chlamydia", "Gonorrhea", "Syphilis", "HPV", "Hepatitis B", "Hepatitis C", "Genital Herpes", "Trichomoniasis", "Others (Please Specify)"];
  const HORMONAL_CONTRACEPTION_OPTIONS = ["IUD", "Implant", "Injection", "Pills", "Others (Please Specify)"];
  // eslint-disable-next-line no-unused-vars
  const PAST_SURGERY_OPTIONS = ["Abdominal Section", "Pelvic Surgery","Others (Please Specify)"];
  // eslint-disable-next-line no-unused-vars
  const CONSULTATION_REASON_OPTIONS = ["Family Planning/Contraception", "STI Testing / Screening", "Reproductive Consultation"];

  // Health Information state
  const [showHealthModal, setShowHealthModal] = useState(false);
  const [healthInfo, setHealthInfo] = useState({
    medicalHistory: [],
    medicalOther: "",
    allergies: [],
    allergyOther: "",
    hormonalAllergyDetails: "",
    smoking: "",
    alcoholUse: "",
    stiHistory: [],
    stiOther: "",
    hivStatus: "",
    hivOther: "",
    lastTestedDate: "",
    genitalInfections: "",
    hormonalContraception: [],
    hormonalContraceptionOther: "",
    pastSurgeries: [],
    pastSurgeryOther: "",
    currentMedications: "",
    emergencyContactName: "",
    emergencyContactRelationship: "",
    emergencyContactRelationshipOther: "",
    emergencyContactNumber: ""
  });
  const [tempHealthInfo, setTempHealthInfo] = useState({ ...healthInfo });
  const [isHealthInfoComplete, setIsHealthInfoComplete] = useState(false);
  const [healthInfoStep, setHealthInfoStep] = useState(0);
  const [contactNumberError, setContactNumberError] = useState("");
  const HEALTH_INFO_STEPS = [
    "Health & Lifestyle",
    "Sexual Health History",
    "Medication History",
    "Emergency Contact"
  ];

  // Toggle pill selection
  const togglePill = (field, option) => {
    setTempHealthInfo(prev => {
      let newArray = [...prev[field]];
      let updatedFields = {};
      
      if (option === "None") {
        newArray = newArray.includes("None") ? [] : ["None"];
      } else {
        newArray = newArray.includes("None") ? [] : newArray;
        if (newArray.includes(option)) {
          newArray = newArray.filter(item => item !== option);
          
          // Reset the corresponding text field when unselecting
          if (option === "Others (Please Specify)" && field === "medicalHistory") {
            updatedFields.medicalOther = "";
          } else if (option === "Others (Please Specify)" && field === "allergies") {
            updatedFields.allergyOther = "";
          } else if (option === "Hormonal Medications (Pills, Patches, Injections)" && field === "allergies") {
            updatedFields.hormonalAllergyDetails = "";
          } else if (option === "Others (Please Specify)" && field === "stiHistory") {
            updatedFields.stiOther = "";
          } else if (option === "Others (Please Specify)" && field === "hormonalContraception") {
            updatedFields.hormonalContraceptionOther = "";
          } else if (option === "Others (Please Specify)" && field === "pastSurgeries") {
            updatedFields.pastSurgeryOther = "";
          }
        } else {
          newArray.push(option);
        }
      }
      
      return { ...prev, [field]: newArray, ...updatedFields };
    });
  };

  const resetBookingFlow = useCallback(() => {
    setExpandedSection("location");
    setCompletedSections({ location: true });
    setSelectedClinic(DEFAULT_CLINIC_ID);
    setSelectedReason("");
    setSelectedDate(getNextBusinessDay(new Date()));
    setSelectedTime("");
    setBookedSlots([]);
    setShowBookingSuccess(false);
    setNewAppointment(null);
    setIsBooking(false);
  }, []);

  // Sync tempHealthInfo when modal opens
  useEffect(() => {
    if (showHealthModal) {
      setTempHealthInfo({ ...healthInfo });
      setHealthInfoStep(0);
      setContactNumberError("");
    }
  }, [showHealthModal, healthInfo]);

  // Validate current step
  const validateCurrentStep = () => {
    switch (healthInfoStep) {
      case 0: // Health & Lifestyle
        if (tempHealthInfo.medicalHistory.includes('Others (Please Specify)') && !tempHealthInfo.medicalOther.trim()) {
          setNotification({ message: "Please specify your medical history when selecting 'Other'", type: "error" });
          return false;
        }
        if (tempHealthInfo.allergies.includes('Others (Please Specify)') && !tempHealthInfo.allergyOther.trim()) {
          setNotification({ message: "Please specify your allergies when selecting 'Other'", type: "error" });
          return false;
        }
        if (userSex !== "male" && tempHealthInfo.allergies.includes('Hormonal Medications (Pills, Patches, Injections)') && !tempHealthInfo.hormonalAllergyDetails.trim()) {
          setNotification({ message: "Please specify details about your hormonal medication allergy", type: "error" });
          return false;
        }
        break;
      case 1: // Sexual Health History
        if (!tempHealthInfo.hivStatus) {
          setNotification({ message: "Please select your HIV status", type: "error" });
          return false;
        }
        if ((tempHealthInfo.hivStatus === 'Negative' || tempHealthInfo.hivStatus === 'Positive') && tempHealthInfo.lastTestedDate) {
          const lastTested = new Date(tempHealthInfo.lastTestedDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (patientDetails.dob) {
            const dob = new Date(patientDetails.dob);
            if (lastTested < dob) {
              setNotification({ message: "Last tested date cannot be before your date of birth", type: "error" });
              return false;
            }
          }
          
          if (lastTested > today) {
            setNotification({ message: "Last tested date cannot be in the future", type: "error" });
            return false;
          }
        }
        if (tempHealthInfo.stiHistory.includes('Others (Please Specify)') && !tempHealthInfo.stiOther.trim()) {
          setNotification({ message: "Please specify your STI history when selecting 'Other'", type: "error" });
          return false;
        }
        break;
      case 2: // Medication History
        if (userSex !== "male" && tempHealthInfo.hormonalContraception.includes('Others (Please Specify)') && !tempHealthInfo.hormonalContraceptionOther.trim()) {
          setNotification({ message: "Please specify your hormonal contraception when selecting 'Other'", type: "error" });
          return false;
        }
        if (tempHealthInfo.pastSurgeries.includes('Others (Please Specify)') && !tempHealthInfo.pastSurgeryOther.trim()) {
          setNotification({ message: "Please specify your past surgeries when selecting 'Other'", type: "error" });
          return false;
        }
        break;
      case 3: // Emergency Contact
        setContactNumberError("");
        if (!tempHealthInfo.emergencyContactName.trim()) {
          setNotification({ message: "Please enter emergency contact name", type: "error" });
          return false;
        }
        if (!tempHealthInfo.emergencyContactRelationship) {
          setNotification({ message: "Please select emergency contact relationship", type: "error" });
          return false;
        }
        if (tempHealthInfo.emergencyContactRelationship === 'Other' && !tempHealthInfo.emergencyContactRelationshipOther.trim()) {
          setNotification({ message: "Please specify emergency contact relationship", type: "error" });
          return false;
        }
        if (!tempHealthInfo.emergencyContactNumber) {
          setNotification({ message: "Please enter emergency contact number", type: "error" });
          setContactNumberError("Please enter emergency contact number");
          return false;
        }
        if (tempHealthInfo.emergencyContactNumber.length < 10 || !tempHealthInfo.emergencyContactNumber.startsWith('9')) {
          setNotification({ message: "Contact number must be 10 digits starting with 9", type: "error" });
          setContactNumberError("Contact number must be 10 digits starting with 9");
          return false;
        }
        break;
      default:
        return true;
    }
    return true;
  };

  const handleSaveHealthInfo = async () => {
    try {
      // Validate all steps
      if (!validateCurrentStep()) {
        return;
      }

      const user = auth.currentUser;
      if (!user) return;

      await userService.saveUser(user.uid, {
        medicalHistory: tempHealthInfo.medicalHistory,
        medicalOther: tempHealthInfo.medicalOther,
        allergies: tempHealthInfo.allergies,
        allergyOther: tempHealthInfo.allergyOther,
        hormonalMedications: tempHealthInfo.hormonalAllergyDetails,
        hormonalAllergyDetails: tempHealthInfo.hormonalAllergyDetails,
        smoking: tempHealthInfo.smoking,
        alcoholUse: tempHealthInfo.alcoholUse,
        stiHistory: tempHealthInfo.stiHistory,
        stiOther: tempHealthInfo.stiOther,
        hivStatus: tempHealthInfo.hivStatus,
        hivOther: tempHealthInfo.genitalInfections,
        lastTestedDate: tempHealthInfo.lastTestedDate,
        genitalInfections: tempHealthInfo.genitalInfections,
        additionalHealthHistory: tempHealthInfo.hormonalContraception,
        hormonalContraception: tempHealthInfo.hormonalContraception,
        contraceptionOther: tempHealthInfo.hormonalContraceptionOther,
        hormonalContraceptionOther: tempHealthInfo.hormonalContraceptionOther,
        pastSurgeries: tempHealthInfo.pastSurgeries,
        surgeryOther: tempHealthInfo.pastSurgeryOther,
        pastSurgeryOther: tempHealthInfo.pastSurgeryOther,
        currentMedications: tempHealthInfo.currentMedications,
        emergencyContactName: tempHealthInfo.emergencyContactName,
        emergencyContactRelationship: tempHealthInfo.emergencyContactRelationship,
        emergencyContactRelationshipOther: tempHealthInfo.emergencyContactRelationshipOther,
        emergencyContactNumber: tempHealthInfo.emergencyContactNumber,
        emergencyContact: {
          name: tempHealthInfo.emergencyContactName,
          relationship: tempHealthInfo.emergencyContactRelationship === 'Other' 
            ? tempHealthInfo.emergencyContactRelationshipOther 
            : tempHealthInfo.emergencyContactRelationship,
          contact: tempHealthInfo.emergencyContactNumber
        }
      });

      setHealthInfo({ ...tempHealthInfo });

      // Check if health info is complete
      const isComplete = (
        tempHealthInfo.hivStatus &&
        tempHealthInfo.emergencyContactName.trim() &&
        tempHealthInfo.emergencyContactRelationship &&
        (tempHealthInfo.emergencyContactRelationship !== 'Other' || tempHealthInfo.emergencyContactRelationshipOther.trim()) &&
        tempHealthInfo.emergencyContactNumber
      );
      setIsHealthInfoComplete(isComplete);

      setNotification({ message: "Health information saved successfully!", type: "success" });
      setShowHealthModal(false);
    } catch (error) {
      console.error("Error saving health info:", error);
      setNotification({ message: "Failed to save health information.", type: "error" });
    }
  };

  // Fetch user details
  useEffect(() => {
    let unsubscribeAppts = null;
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (unsubscribeAppts) {
        unsubscribeAppts();
        unsubscribeAppts = null;
      }

      if (user) {
          // Fetch actual user details from Firestore
          try {
            const userData = await userService.getUser(user.uid);
            
            if (userData) {
              // Set user sex
              setUserSex(userData.sex || userData.personalInformation?.sex || "");
              
              const fetchedName = getPatientFullName(userData, user);
              
              // Extract guardian data before setting patient details
              const extractedData = userService.extractUserData(userData);
              const isAssistedAccount = extractedData.assistanceRequired === "yes";
              const reasonForAssistance = extractedData.reasonForAssistance || [];
              const isMinor = Array.isArray(reasonForAssistance) ? reasonForAssistance.includes("minor") : reasonForAssistance === "minor";

              let guardianFullName = "";
              let guardianRelationship = "";
              let guardianContact = "";
              let guardianAddress = "";

              if (isMinor) {
                guardianFullName = extractedData.guardianFullName || "";
                guardianRelationship = "Guardian";
                guardianContact = extractedData.guardianContactNumber ? `+63${getLast10Digits(extractedData.guardianContactNumber)}` : "";
                guardianAddress = extractedData.guardianAddress || "";
              } else if (isAssistedAccount) {
                guardianFullName = extractedData.accompanistFullName || "";
                guardianRelationship = "Other";
                guardianContact = extractedData.accompanistContactNumber ? `+63${getLast10Digits(extractedData.accompanistContactNumber)}` : "";
                guardianAddress = extractedData.accompanistAddress || "";
              }
              
              setPatientDetails(prev => ({
                ...prev,
                fullName: fetchedName,
                email: userData.email || user.email || "",
                phone: userData.phone || prev.phone || "",
                dob: userData.dob || userData.dateOfBirth || "",
                guardianFullName: guardianFullName || "",
                guardianRelationship: guardianRelationship || "",
                guardianContact: guardianContact || "",
                guardianAddress: guardianAddress || "",
                assistanceRequired: isAssistedAccount ? "yes" : ""
              }));

              // Set health information
              const existingEmergencyName = userData.emergencyContact?.name || userData.emergencyContactName || "";
              const existingEmergencyContact = getLast10Digits(userData.emergencyContact?.contact || userData.emergencyContactNumber || "");

              const healthData = {
                medicalHistory: normalizeSelectionList(extractedData.medicalHistory, OTHER_ALIAS),
                medicalOther: extractedData.medicalOther || "",
                allergies: normalizeSelectionList(extractedData.allergies, OTHER_ALIAS),
                allergyOther: extractedData.allergyOther || "",
                hormonalAllergyDetails: extractedData.hormonalMedications || extractedData.hormonalAllergyDetails || "",
                smoking: extractedData.smoking || "",
                alcoholUse: extractedData.alcoholUse || "",
                stiHistory: normalizeSelectionList(extractedData.stiHistory, STI_ALIASES),
                stiOther: extractedData.stiOther || "",
                hivStatus: extractedData.hivStatus || "",
                hivOther: extractedData.hivOther || "",
                lastTestedDate: extractedData.lastTestedDate || "",
                genitalInfections: extractedData.hivOther || extractedData.genitalInfections || "",
                hormonalContraception: normalizeSelectionList(extractedData.additionalHealthHistory || extractedData.hormonalContraception || [], OTHER_ALIAS),
                hormonalContraceptionOther: extractedData.contraceptionOther || extractedData.hormonalContraceptionOther || "",
                pastSurgeries: normalizeSelectionList(extractedData.pastSurgeries, SURGERY_ALIASES),
                pastSurgeryOther: extractedData.surgeryOther || extractedData.pastSurgeryOther || "",
                currentMedications: extractedData.currentMedications || "",
                emergencyContactName: existingEmergencyName || (isAssistedAccount ? guardianFullName : ""),
                emergencyContactRelationship: userData.emergencyContact?.relationship 
                  ? (["Parent","Partner","Guardian","Sibling","Relative","Other"].includes(userData.emergencyContact.relationship) 
                    ? userData.emergencyContact.relationship 
                    : "Other") 
                  : userData.emergencyContactRelationship || (isAssistedAccount && !existingEmergencyName && !existingEmergencyContact ? guardianRelationship : ""),
                emergencyContactRelationshipOther: userData.emergencyContactRelationshipOther 
                  || (userData.emergencyContact?.relationship 
                    && !["Parent","Partner","Guardian","Sibling","Relative","Other"].includes(userData.emergencyContact.relationship) 
                      ? userData.emergencyContact.relationship 
                      : (isAssistedAccount && !existingEmergencyName && !existingEmergencyContact && !isMinor ? extractedData.accompanistRelationshipToUser : "")),
                emergencyContactNumber: existingEmergencyContact || (isAssistedAccount ? getLast10Digits(guardianContact) : "")
              };
              setHealthInfo(healthData);
              
              // Check if health info is complete
              const isComplete = (
                healthData.hivStatus &&
                healthData.emergencyContactName.trim() &&
                healthData.emergencyContactRelationship &&
                (healthData.emergencyContactRelationship !== 'Other' || healthData.emergencyContactRelationshipOther.trim()) &&
                healthData.emergencyContactNumber
              );
              setIsHealthInfoComplete(isComplete);
            } else {
              console.warn("User data is null, using Auth fallback.");
              setPatientDetails(prev => ({
                ...prev,
                fullName: user.displayName || "User",
                email: user.email || ""
              }));
            }
          } catch (err) {
            console.error("Error fetching user data:", err);
            setPatientDetails(prev => ({
              ...prev,
              fullName: user.displayName || "User",
              email: user.email || ""
            }));
          }
        
        // Initial fetch
        // Real-time listener for user's appointments (removed redundant fetchAppts call)
        const q = query(
          collection(db, 'appointments'),
          where('uid', '==', user.uid),
          orderBy("createdAt", "desc"),
          limit(50) // Add limit
        );

        let mounted = true;

        unsubscribeAppts = onSnapshot(q, (snapshot) => {
          const current = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          // In-memory sort by createdAt descending (newest first)
          // Safe handler: createdAt may be a Firestore Timestamp, ISO string, or number
          const toMs = (v) => {
            if (!v) return 0;
            if (typeof v.toMillis === 'function') return v.toMillis();
            if (typeof v === 'string') { const ms = Date.parse(v); return isNaN(ms) ? 0 : ms; }
            if (typeof v === 'number') return v;
            return 0;
          };
          const sorted = current.sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));
          setMyAppointments(sorted);
          setIsLoadingAppts(false); // Set loading false when listener receives data
        }, (err) => {
          console.error("Error in real-time listener:", err);
          setIsLoadingAppts(false);
        });

        // Store mounted ref for cleanup
        apptsMountedRef.current = mounted;

      } else {
        setPatientDetails({
          fullName: "",
          email: "",
          phone: "",
          dob: ""
        });
        setMyAppointments([]);
      }
    });

    return () => {
      if (apptsMountedRef.current) apptsMountedRef.current = false;
      if (unsubscribeAppts) unsubscribeAppts();
      unsubscribe();
    };
  }, []);

  // Fetch appointments (kept for manual refresh, but primary data comes from real-time listener)
  const fetchAppts = async (uid) => {
    if (!uid) {
      console.warn("fetchAppts called without UID");
      return;
    }
    
    if (!auth.currentUser) {
      console.warn("fetchAppts called but auth.currentUser is null");
    }

    setIsLoadingAppts(true);
    try {
      const all = await appointmentService.getUserAppointments(uid);
      setMyAppointments(all);
    } catch (err) {
      console.error("Error fetching appointments:", err);
    } finally {
      setIsLoadingAppts(false);
    }
  };

  // Removed redundant fetch on nav change - real-time listener handles updates

  // Subscribe to online booked slots for selected date and service - OPTIMIZED
  // Query by date only to avoid Firestore composite-index requirements on Spark Plan.
  useEffect(() => {
    if (!selectedReason || !selectedDate) {
      setBookedSlots([]);
      return;
    }

    const dateStr = formatDateToPH(selectedDate);
    const selectedServiceName = VISIT_REASONS.find(r => r.id === selectedReason)?.title || selectedReason;
    
    const q = query(
      collection(db, 'appointments'),
      where('date', '==', dateStr),
      where('serviceId', '==', selectedReason),
      where('status', 'in', ['PENDING', 'CONFIRMED', 'ACCEPTED']),
      limit(100) // Add limit
    );

    let mounted = true;

    const unsubscribeSlots = onSnapshot(q, (snapshot) => {
      if (!mounted) return;
      
      const reservedTimes = snapshot.docs
        .map(doc => doc.data())
        .filter(appt => {
          const isOnlineBooking = appt.type !== "walk-in";
          return isOnlineBooking && appt.time;
        })
        .map(appt => appt.time);

      setBookedSlots([...new Set(reservedTimes)]);
    }, (err) => {
      console.error("Error subscribing to booked slots:", err);
      setBookedSlots([]);
    });

    return () => {
      mounted = false;
      if (slotsMountedRef.current) slotsMountedRef.current = false;
      unsubscribeSlots();
    };
  }, [selectedDate, selectedReason]);

  useEffect(() => {
    setSelectedTime("");
    setCompletedSections(prev => {
      if (!prev.datetime && !prev.patient) return prev;
      const next = { ...prev };
      delete next.datetime;
      delete next.patient;
      return next;
    });
  }, [selectedDate, selectedReason]);

  // Ensure selectedDate is never a weekend (safety net)
  useEffect(() => {
    if (isWeekend(selectedDate)) {
      const nextBusinessDay = getNextBusinessDay(selectedDate);
      setSelectedDate(nextBusinessDay);
      setNotification({ message: "The clinic is closed on weekends. Automatically selected the next available business day.", type: "error" });
    }
  }, [selectedDate]);

  const handleSectionClick = (section) => {
    const currentIndex = BOOK_STEPS.indexOf(section);
    const prevSection = BOOK_STEPS[currentIndex - 1];
    
    if (completedSections[section]) {
      setExpandedSection(section);
    } else if (currentIndex === 0 || completedSections[prevSection]) {
      setExpandedSection(section);
    }
  };

  const handleReasonSelect = (reasonId) => {
    setSelectedReason(reasonId);
    setSelectedTime("");
    setCompletedSections(prev => ({ location: prev.location || true, reason: true }));
    setExpandedSection("datetime");
  };

  const isTimeSlotValid = (time) => {
    const phNow = getPHNow();
    const slotTotalMinutes = timeLabelToMinutes(time);
    const selectedDateStr = formatDateToPH(selectedDate);
    const todayPHStr = formatDateToPH(new Date());
    const isToday = selectedDateStr === todayPHStr;
    const isPastDate = selectedDateStr < todayPHStr;

    if (isPastDate) return { valid: false, reason: "past" };

    if (isToday) {
      const nowTotalMinutes = phNow.hour * 60 + phNow.minute;
      if (slotTotalMinutes < nowTotalMinutes + 60) {
        return { valid: false, reason: "at_least_1hr" };
      }
    }

    if (bookedSlots.includes(time)) return { valid: false, reason: "booked" };

    const validation = getSlotValidation(time, selectedReason);
    if (!validation.valid) {
      return { valid: false, reason: validation.reason === "lunch" ? "lunch" : "service" };
    }

    const slotRule = SERVICE_SLOT_RULES[selectedReason] || SERVICE_SLOT_RULES.rh_consult;
    if (slotTotalMinutes < 8 * 60 || slotTotalMinutes >= slotRule.close) {
      return { valid: false, reason: "service" };
    }

    return { valid: true };
  };

  const handleSelectTime = async (time) => {
    const { valid, reason } = isTimeSlotValid(time);
    
    if (!valid) {
      if (reason === "past") {
        setNotification({ message: "This time slot has already passed. Please choose a future time.", type: "error" });
      } else if (reason === "at_least_1hr") {
        setNotification({ message: "You can only book at least 1 hour in advance. Please select a later time.", type: "error" });
      } else if (reason === "booked") {
        setNotification({ message: "This time slot is no longer available. Please select another.", type: "error" });
      } else if (reason === "service") {
        setNotification({ message: "This time slot is outside the selected service's booking hours or lunch break.", type: "error" });
      } else if (reason === "lunch") {
        setNotification({ message: "The clinic is closed for lunch from 12:00 PM to 1:00 PM. Please choose another time.", type: "error" });
      }
      return;
    }
    
    if (bookedSlots.includes(time)) {
      setNotification({ message: "This time slot was just booked by someone else. Please select another.", type: "error" });
      return;
    }

    setSelectedTime(time);
    if (selectedDate && time) {
      setCompletedSections(prev => ({ ...prev, datetime: true }));
      setExpandedSection("patient");
    }
  };

  const handleScheduleAppointment = async () => {
    if (isBooking) return;
    setIsBooking(true);

    const user = auth.currentUser;
    if (!user) {
      alert("Please log in to book an appointment.");
      setIsBooking(false);
      return;
    }
    if (!selectedReason || !selectedDate || !selectedTime) {
      alert("Please select a visit reason, date, and time before scheduling.");
      setIsBooking(false);
      return;
    }
    
    // Prevent weekend bookings
    if (isWeekend(selectedDate)) {
      setNotification({ message: "The clinic is closed on weekends. Please select a weekday.", type: "error" });
      setIsBooking(false);
      return;
    }

    try {
      const appointmentDate = formatDateToPH(selectedDate);
      const selectedService = VISIT_REASONS.find(r => r.id === selectedReason);
      const apptData = {
        uid: user.uid,
        proId: DEFAULT_MEDICAL_PROFESSIONAL.uid,
        proName: DEFAULT_MEDICAL_PROFESSIONAL.fullName,
        patientName: patientDetails.fullName,
        patientEmail: patientDetails.email,
        patientPhone: patientDetails.phone,
        patientDob: patientDetails.dob,
        clinicId: selectedClinic,
        clinicName: CLINICS.find(c => c.id === selectedClinic)?.name,
        serviceId: selectedReason,
        serviceName: selectedService?.title,
        date: appointmentDate,
        time: selectedTime,
        reference: generateReference(),
        status: 'PENDING'
      };

      const result = await appointmentService.createAppointment(apptData);
      setNewAppointment(result);
      setShowBookingSuccess(true);
      setCompletedSections(prev => ({ ...prev, patient: true }));
    } catch (err) {
      alert(err.message || "Failed to schedule appointment.");
    } finally {
      setIsBooking(false);
    }
  };

  // ----- Calendar / Time slots -----
  const todayPHStr = formatDateToPH(new Date());
  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const getCalendarDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    const totalDays = lastDay.getDate();
    
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let day = 1; day <= totalDays; day++) cells.push(day);
    return cells;
  };

  const calendarDays = getCalendarDays(selectedDate);
  
  // Helper to check if a date is a weekend (Saturday or Sunday)
  const isWeekend = (date) => {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
  };
  
  // Handler for weekend date clicks
  const handleWeekendClick = () => {
    setNotification({ message: "The clinic is closed on weekends. Please select a weekday.", type: "error" });
  };
  
  const generateTimeSlots = () => {
    // Prevent time slot generation on weekends
    if (isWeekend(selectedDate)) {
      return { morning: [], afternoon: [] };
    }

    const slotRule = SERVICE_SLOT_RULES[selectedReason] || SERVICE_SLOT_RULES.rh_consult;
    const slots = { morning: [], afternoon: [] };

    for (let minutes = 8 * 60; minutes < slotRule.close; minutes += slotRule.interval) {
      // Do not render lunch-break slots in the user booking flow.
      if (minutes >= 12 * 60 && minutes < 13 * 60) continue;

      const label = minutesToTimeLabel(minutes);
      if (minutes < 12 * 60) {
        slots.morning.push(label);
      } else {
        slots.afternoon.push(label);
      }
    }

    return slots;
  };
  const timeSlots = generateTimeSlots();

  const handleCancelToHistory = (appt) => {
    setApptToCancel(appt);
    setShowCancelModal(true);
  };

  const handleConfirmCancel = async (appt, finalReason) => {
    if (!appt) return;
    
    setIsCancelling(true);
    try {
      const newStatus = "CANCELLED_BY_USER";
      await appointmentService.updateStatus(appt.id, newStatus, { 
        cancelReason: finalReason,
        cancelledAt: new Date().toISOString()
      });
      
      setNotification({ message: "Appointment cancelled successfully.", type: "success" });
      
      // Refresh list
      const user = auth.currentUser;
      if (user) {
        await fetchAppts(user.uid);
      }
      
      setShowCancelModal(false);
      setApptToCancel(null);
    } catch (err) {
      setNotification({ message: "Failed to cancel appointment. Please try again.", type: "error" });
    } finally {
      setIsCancelling(false);
    }
  };

  const getClinicLabel = () => CLINICS.find(c => c.id === selectedClinic)?.name || "";
  const getReasonLabel = () => VISIT_REASONS.find(r => r.id === selectedReason)?.title || "";
  const getDateTimeLabel = () => {
    if (!selectedDate || !selectedTime) return "";
    return `${selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} • ${selectedTime}`;
  };

  const BookingSection = ({ title, sectionKey, isCompleted, displayValue, children, isDisabled, isNextAvailable }) => (
    <div className={`booking-section ${isDisabled ? 'disabled' : ''}`}>
      <div 
        className={`booking-section-header ${isCompleted ? 'completed' : ''} ${isNextAvailable ? 'next-available' : ''} ${expandedSection === sectionKey ? 'expanded' : ''}`}
        onClick={() => !isDisabled && handleSectionClick(sectionKey)}
      >
        <div className="section-title-row">
          {(isCompleted || isNextAvailable) && <div className="check-icon">✓</div>}
          <h3>{title}</h3>
          {isCompleted && displayValue && <span className="section-value">{displayValue}</span>}
        </div>
      </div>
      {expandedSection === sectionKey && <div className="booking-section-content">{children}</div>}
    </div>
  );

  const renderBookAppointment = () => (
    <>
      <div className="booking-flow-container">
        <div className="booking-left-column">
          <BookingSection title="CONFIRM LOCATION" sectionKey="location" isCompleted={true} displayValue={getClinicLabel()}>
            <div className="booking-options">
              {CLINICS.map((clinic) => (
                <div key={clinic.id} className="booking-option only-option selected" style={{ cursor: 'default' }}>
                  <div className="option-content">
                    <div className="option-title">{clinic.name}</div>
                    <div className="option-description">{clinic.address}</div>
                  </div>
                </div>
              ))}
            </div>
          </BookingSection>

          <BookingSection title="VISIT REASON" sectionKey="reason" isCompleted={completedSections.reason} displayValue={getReasonLabel()}>
            <div className="booking-options">
              {VISIT_REASONS.map((reason) => (
                <div key={reason.id} className="booking-option" onClick={() => handleReasonSelect(reason.id)}>
                  <div className={`custom-radio-circle ${selectedReason === reason.id ? 'checked' : ''}`}></div>
                  <div className="option-content">
                    <div className="option-title">{reason.title}</div>
                    <div className="option-description">{reason.description}</div>
                    <div className="option-hours" style={{ 
                      marginTop: '4px', 
                      fontSize: '12px', 
                      color: '#3b82f6', 
                      fontWeight: 600 
                    }}>
                      Service Hours: {reason.hours}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </BookingSection>

          <BookingSection title="SELECT DATE & TIME" sectionKey="datetime" isCompleted={completedSections.datetime} displayValue={getDateTimeLabel()} isDisabled={!completedSections.reason}>
            <div className="datetime-section">
              <div className="calendar-container">
                <div className="calendar-header">
                  <button onClick={() => {
                    const newDate = new Date(selectedDate);
                    newDate.setMonth(newDate.getMonth() - 1);
                    setSelectedDate(newDate);
                  }}>‹</button>
                  <h3>{selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase()}</h3>
                  <button onClick={() => {
                    const newDate = new Date(selectedDate);
                    newDate.setMonth(newDate.getMonth() + 1);
                    setSelectedDate(newDate);
                  }}>›</button>
                </div>
                <div className="calendar-grid">
                  {daysOfWeek.map((day) => <div key={day} className="calendar-day-label">{day}</div>)}
                  {calendarDays.map((day, idx) => {
                    if (!day) return <div key={idx} className="calendar-day empty"></div>;
                    
                    const currentDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
                    const currentDateStr = formatDateToPH(currentDate);
                    const isPastDate = currentDateStr < todayPHStr;
                    const isWeekendDate = isWeekend(currentDate);
                    
                    return (
                      <button
                        key={idx}
                        type="button"
                        className={`calendar-day ${isPastDate ? 'past' : ''} ${isWeekendDate ? 'weekend' : ''} ${selectedDate.getDate() === day ? 'selected' : ''}`}
                        disabled={isPastDate || isWeekendDate}
                        title={isWeekendDate ? "Clinic closed on weekends." : ""}
                        onClick={() => {
                          if (isWeekendDate) {
                            handleWeekendClick();
                          } else {
                            setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day));
                          }
                        }}
                      >{day}</button>
                    );
                  })}
                </div>
              </div>
              
              <div className="time-slots-container">
                <div style={{ 
                  gridColumn: '1 / -1', 
                  backgroundColor: '#fffbeb', 
                  border: '1px solid #fef3c7', 
                  borderRadius: '8px', 
                  padding: '12px 16px', 
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{ color: '#d97706', fontSize: '18px' }}>⚠️</div>
                  <p style={{ margin: 0, fontSize: '14px', color: '#92400e', fontWeight: 500 }}>
                    You can only book at least 1 hour in advance. Please select a later time.
                  </p>
                </div>
                <div className="time-period">
                  <h4>MORNING</h4>
                  <div className="time-slots-grid">
                    {timeSlots.morning.map((time) => {
                      const { valid, reason } = isTimeSlotValid(time);
                      const isBooked = reason === "booked";
                      const isPastHour = reason === "at_least_1hr";
                      return (
                        <button 
                          key={time} 
                          type="button" 
                          className={`time-slot ${selectedTime === time ? 'selected' : ''} ${!valid ? 'disabled-visual' : ''}`} 
                          onClick={() => handleSelectTime(time)}
                          title={isPastHour ? "Hour has already passed" : isBooked ? "Already booked" : ""}
                          style={{ position: 'relative' }}
                        >
                          {time}
                          {isBooked && (
                            <div 
                              style={{ 
                                position: 'absolute', 
                                bottom: '4px', 
                                left: '50%', 
                                transform: 'translateX(-50%)', 
                                width: '8px', 
                                height: '8px', 
                                backgroundColor: '#19458c', 
                                borderRadius: '50%' 
                              }} 
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="time-period">
                  <h4>AFTERNOON</h4>
                  <div className="time-slots-grid">
                    {timeSlots.afternoon.map((time) => {
                      const { valid, reason } = isTimeSlotValid(time);
                      const isBooked = reason === "booked";
                      const isPastHour = reason === "at_least_1hr";
                      return (
                        <button 
                          key={time} 
                          type="button" 
                          className={`time-slot ${selectedTime === time ? 'selected' : ''} ${!valid ? 'disabled-visual' : ''}`} 
                          onClick={() => handleSelectTime(time)}
                          title={isPastHour ? "Hour has already passed" : isBooked ? "Already booked" : ""}
                          style={{ position: 'relative' }}
                        >
                          {time}
                          {isBooked && (
                            <div 
                              style={{ 
                                position: 'absolute', 
                                bottom: '4px', 
                                left: '50%', 
                                transform: 'translateX(-50%)', 
                                width: '8px', 
                                height: '8px', 
                                backgroundColor: '#19458c', 
                                borderRadius: '50%' 
                              }} 
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </BookingSection>

          <BookingSection title="PATIENT DETAILS" sectionKey="patient" isCompleted={completedSections.patient} isDisabled={!completedSections.datetime}>
            <div className="patient-details-form">
              <div className="form-group">
                <label>Full Name</label>
                <input type="text" value={patientDetails.fullName} readOnly className="disabled-input" />
              </div>
              <div className="form-group">
                <label>Date of Birth</label>
                <input type="text" value={patientDetails.dob} readOnly className="disabled-input" />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={patientDetails.email} readOnly className="disabled-input" />
              </div>
              <div className="form-group">
                <label>Contact Number</label>
                <input type="tel" value={patientDetails.phone} readOnly className="disabled-input" />
              </div>
              {(
                patientDetails.assistanceRequired === "yes" &&
                (patientDetails.guardianFullName || patientDetails.guardianRelationship || patientDetails.guardianContact || patientDetails.guardianAddress)
              ) && (
                <>
                  <div className="form-group">
                    <label>Guardian/Accompanist Full Name</label>
                    <input type="text" value={patientDetails.guardianFullName} readOnly className="disabled-input" />
                  </div>
                  <div className="form-group">
                    <label>Relationship</label>
                    <input type="text" value={patientDetails.guardianRelationship} readOnly className="disabled-input" />
                  </div>
                  <div className="form-group">
                    <label>Contact Number</label>
                    <input type="tel" value={patientDetails.guardianContact} readOnly className="disabled-input" />
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <input type="text" value={patientDetails.guardianAddress} readOnly className="disabled-input" />
                  </div>
                </>
              )}
              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontSize: '15px', fontWeight: 600, color: '#64748b' }}>Medical History</label>
                <button
                  type="button"
                  onClick={() => setShowHealthModal(true)}
                  style={{
                    width: '100%',
                    padding: '18px 22px',
                    borderRadius: '20px',
                    border: isHealthInfoComplete ? '2px solid #2563eb' : '2px solid #e2e8f0',
                    background: isHealthInfoComplete ? '#eff6ff' : '#ffffff',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.12)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.borderColor = '#2563eb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = isHealthInfoComplete ? '#2563eb' : '#e2e8f0';
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: '17px', fontWeight: 700, color: '#0f172a' }}>Health Information</span>
                    <span style={{ fontSize: '13.5px', color: '#64748b', lineHeight: 1.4 }}>Required before booking. Click to edit information</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 18px',
                      borderRadius: '9999px',
                      backgroundColor: isHealthInfoComplete ? '#dbeafe' : '#f1f5f9',
                      color: isHealthInfoComplete ? '#1d4ed8' : '#64748b'
                    }}>
                      {isHealthInfoComplete ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: '#1d4ed8' }}>
                          <path d="M20 6L9 17L4 12" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : null}
                      <span style={{ 
                        fontSize: '13px', 
                        fontWeight: 700, 
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em'
                      }}>
                        {isHealthInfoComplete ? 'Complete' : 'Incomplete'}
                      </span>
                    </div>
                    <div style={{ 
                      width: '36px', 
                      height: '36px', 
                      borderRadius: '50%', 
                      backgroundColor: isHealthInfoComplete ? '#2563eb' : '#e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: isHealthInfoComplete ? 'white' : '#64748b'
                    }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M9 18L15 12L9 6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </BookingSection>

          <div className="booking-actions">
            <button type="button" className={`primary-btn ${(!completedSections.datetime || isBooking || !isHealthInfoComplete) ? 'disabled' : ''}`} onClick={handleScheduleAppointment} disabled={!completedSections.datetime || isBooking || !isHealthInfoComplete}>
              {isBooking ? "Scheduling..." : "Schedule an Appointment"}
            </button>
            <button type="button" className="secondary-btn" onClick={() => {
              resetBookingFlow();
              setActiveNav("My Appointments");
              setHistoryActiveTab("upcoming");
            }}>Cancel</button>
          </div>
        </div>

        {selectedClinic && (
          <div className="booking-right-column">
            <div className="clinic-info">
              <div className="map-container">
                <iframe
                  title="Clinic Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3861.986835150829!2d120.9926839!3d14.5427958!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3397c947702f37c3%3A0xc00570a273295874!2s2442%20Park%20Ave%2C%20Pasay%2C%20Metro%20Manila!5e0!3m2!1sen!2sph!4v1713180000000!5m2!1sen!2sph"
                  width="100%"
                  height="250"
                  style={{ border: 0, borderRadius: '12px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
                <a 
                  href="https://www.google.com/maps/search/?api=1&query=2442+Park+Ave,+Pasay+City,+Metro+Manila" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="view-map-link"
                  style={{ display: 'block', marginTop: '10px', color: '#29499d', fontWeight: 600, textDecoration: 'underline', fontSize: '14px' }}
                >
                  View on Google Maps
                </a>
              </div>
              <div className="clinic-details">
                <div className="clinic-location">
                  <span className="location-city">Park Avenue Clinic Branch</span>
                </div>
                <div className="clinic-address">
                  <span className="location-state">PH, Park Avenue Clinic Branch</span>
                  <span className="location-street">123 Park Avenue, Pasay City</span>
                </div>
                <div className="clinic-phone">
                  <span className="phone-label">Phone - </span>
                  <span className="phone-number">(02) 8123 4567</span>
                </div>
                <div className="clinic-hours">
                  <h4>SERVICES HOURS</h4>
                  <p>Monday - Friday</p>
                  <p>Reproductive Health Consultation: 8 AM to 5 PM</p>
                  <p>Family Planning / Contraceptive Services: 8 AM to 3 PM</p>
                  <p>STI Testing / Screening: 8 AM to 5 PM</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );

  const renderMainContent = () => {
    if (isLoadingAppts) return <div className="loading-state">Loading your appointments...</div>;

    switch (activeNav) {
      case "Book Appointment":
        return renderBookAppointment();
      case "My Appointments":
        return <MyAppointments 
          appointments={myAppointments} 
          onConfirmCancel={handleCancelToHistory}
          onNavigateToHistory={(tab) => {
            setActiveNav("My Appointments");
            setHistoryActiveTab(tab);
          }}
          initialTab={historyActiveTab}
        />;
      default:
        return renderBookAppointment();
    }
  };

  const handleNavChange = (nav) => {
    setActiveNav(nav);
    if (nav === "My Appointments") {
      setHistoryActiveTab("upcoming");
    } else if (nav === "Book Appointment") {
      resetBookingFlow();
    }
  };

  return (
    <section className="appointments-container">
      <div className="appointments-header">
        <div className="appointments-header-text">
          <h1>{t.appointments}</h1>
        </div>
        <div className="appointments-nav-toggle" style={{ alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              className={`nav-toggle-btn ${activeNav === "Book Appointment" ? "active" : ""}`}
              onClick={() => handleNavChange("Book Appointment")}
            >
              {t.bookAppointment}
            </button>
            <button
              className={`nav-toggle-btn ${activeNav === "My Appointments" ? "active" : ""}`}
              onClick={() => handleNavChange("My Appointments")}
            >
              {t.myAppointments}
            </button>
          </div>
        </div>
      </div>

      <div className="appointments-content">
        <div className="appointments-workspace">{renderMainContent()}</div>
      </div>

      <NotificationModal 
        message={notification.message} 
        type={notification.type} 
        onClose={() => setNotification({ message: "", type: "" })} 
      />

      {showBookingSuccess && newAppointment && (
        <div className="appointments-modal-backdrop">
          <div className="appointments-modal-card appointments-booking-success-modal" style={{
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(30, 58, 138, 0.25)',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '620px',
            width: '92%',
            padding: '0'
          }}>
            {/* Header with gradient background */}
            <div style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 25%, #1e293b 75%, #0f172a 100%)',
              backgroundSize: '400% 400%',
              animation: 'sidebarGradientShift 12s ease-in-out infinite',
              padding: '32px 32px',
              textAlign: 'center',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.2)',
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
              }}>
                <span style={{ fontSize: '36px', color: 'white' }}>✓</span>
              </div>
              <h3 style={{ 
                fontSize: '26px', 
                margin: 0, 
                color: 'white', 
                fontWeight: 700,
                letterSpacing: '-0.3px'
              }}>Booking Successful!</h3>
              <p style={{ 
                margin: '8px 0 0', 
                color: 'rgba(255,255,255,0.85)', 
                fontSize: '14px',
                fontWeight: 500
              }}>Your appointment has been scheduled</p>
            </div>

            {/* Appointment details body */}
            <div style={{ 
              padding: '28px 32px 24px', 
              background: '#ffffff', 
              maxHeight: '380px', 
              overflowY: 'auto',
              paddingRight: '36px',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <h4 style={{ 
                fontSize: '16px', 
                fontWeight: 700, 
                color: '#1e293b',
                margin: '0 0 20px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(226, 232, 240, 0.9)'
              }}>Appointment Details</h4>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '14px' }}>
                {[
                  { label: 'Reference #', value: newAppointment.reference },
                  { label: 'Patient', value: newAppointment.patientName },
                  { label: 'Clinic', value: newAppointment.clinicName },
                  { label: 'Service', value: newAppointment.serviceName },
                  { label: 'Professional', value: newAppointment.proName || 'To Be Assigned' },
                  { label: 'Date', value: newAppointment.date },
                  { label: 'Time', value: newAppointment.time },
                  { label: 'Status', value: 'Pending Approval' }
                ].map((item, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    background: '#f8fafc',
                    borderRadius: '12px',
                    border: '1px solid rgba(226, 232, 240, 0.9)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 12px rgba(59, 130, 246, 0.08)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                  >
                    <span style={{ 
                      color: '#64748b', 
                      fontWeight: 600,
                      fontSize: '13px'
                    }}>{item.label}:</span>
                    <span style={{ 
                      color: '#1e293b', 
                      fontWeight: 700,
                      fontSize: '14px'
                    }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer with button */}
            <div style={{ 
              padding: '20px 32px 28px', 
              background: '#fafbff',
              borderTop: '1px solid rgba(226, 232, 240, 0.9)',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <button 
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  borderRadius: '14px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 25%, #1e293b 75%, #0f172a 100%)',
                  backgroundSize: '400% 400%',
                  animation: 'sidebarGradientShift 12s ease-in-out infinite',
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 16px rgba(37, 99, 235, 0.25)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.35)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(37, 99, 235, 0.25)';
                }}
                onClick={() => {
                  setActiveNav("My Appointments");
                  setHistoryActiveTab("upcoming");
                  resetBookingFlow();
                }}
              >
                View My Appointments
              </button>
            </div>
          </div>
        </div>
      )}

      {showHealthModal && (
        <div className="appointments-modal-backdrop" onClick={() => setShowHealthModal(false)}>
          <div 
            style={{ 
              maxWidth: '680px', 
              width: '94%', 
              maxHeight: '85vh', 
              borderRadius: '24px', 
              overflow: 'hidden', 
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.2)', 
              position: 'relative',
              background: '#ffffff'
            }}
            onClick={e => e.stopPropagation()}
          >
            <button 
              type="button" 
              onClick={() => setShowHealthModal(false)} 
              style={{ 
                position: 'absolute',
                top: '14px',
                right: '14px',
                zIndex: 20,
                width: '38px', 
                height: '38px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.18)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white',
                fontSize: '22px',
                lineHeight: 1,
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(6px)'
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.32)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.18)'}
            >✕</button>

            <div style={{ 
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #1e40af 100%)',
              padding: '24px 24px 18px 24px',
              color: 'white',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <div style={{ marginBottom: '16px' }}>
                <h2 style={{ margin: 0, fontSize: '21px', fontWeight: 700, letterSpacing: '-0.3px' }}>Health Information</h2>
                <p style={{ margin: '6px 0 0 0', fontSize: '14px', opacity: 0.92, fontWeight: 400 }}>Only the required fields need to be completed. Please provide your <strong>HIV status</strong> and <strong>emergency contact information</strong>. You may complete the remaining fields at your convenience.</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ 
                  background: 'rgba(255,255,255,0.2)', 
                  padding: '6px 14px', 
                  borderRadius: '9999px',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  letterSpacing: '0.02em'
                }}>
                  Step {healthInfoStep + 1} of {HEALTH_INFO_STEPS.length}
                </div>
                <div style={{ fontSize: '14px', fontWeight: 600, opacity: 0.98 }}>
                  {HEALTH_INFO_STEPS[healthInfoStep]}
                </div>
              </div>
            </div>

            <div style={{ padding: '16px 24px 0 24px' }}>
              <div style={{ display: 'flex', gap: '5px', height: '5px' }}>
                {HEALTH_INFO_STEPS.map((step, idx) => (
                  <div 
                    key={idx} 
                    style={{
                      flex: 1,
                      height: '100%',
                      borderRadius: '9999px',
                      backgroundColor: idx <= healthInfoStep ? '#3b82f6' : '#e2e8f0',
                      transition: 'background-color 0.3s ease'
                    }}
                  />
                ))}
              </div>
            </div>

            <div style={{ 
              padding: '18px 24px 20px 24px', 
              overflowY: 'auto', 
              maxHeight: '52vh'
            }}>
              {/* Step 1: Health & Lifestyle */}
              {healthInfoStep === 0 && (
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 18px 0' }}>Health & Lifestyle</h3>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>Health / Medical History</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                      {MEDICAL_HISTORY_OPTIONS.map(option => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => togglePill('medicalHistory', option)}
                          style={{
                            flex: '1 1 calc(50% - 4px)',
                            padding: '8px 16px',
                            borderRadius: '9999px',
                            border: tempHealthInfo.medicalHistory.includes(option) ? '2px solid #2563eb' : '2px solid #e2e8f0',
                            backgroundColor: tempHealthInfo.medicalHistory.includes(option) ? '#2563eb' : '#ffffff',
                            color: tempHealthInfo.medicalHistory.includes(option) ? '#ffffff' : '#475569',
                            fontSize: '13.5px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.18s ease',
                            minWidth: 'fit-content'
                          }}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                    {tempHealthInfo.medicalHistory.includes('Others (Please Specify)') && (
                      <TextareaWithCounter
                        value={tempHealthInfo.medicalOther}
                        onChange={(val) => setTempHealthInfo({ ...tempHealthInfo, medicalOther: val })}
                        placeholder="Please specify"
                        required={tempHealthInfo.medicalHistory.includes('Others (Please Specify)')}
                      />
                    )}
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>Medication Allergies</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                      {(() => {
                        let allergyOptions = ["Penicillin / Antibiotics"];
                        if (userSex !== "male") {
                          allergyOptions.push("Hormonal Medications (Pills, Patches, Injections)");
                        }
                        allergyOptions.push("Others (Please Specify)");
                        return allergyOptions;
                      })().map(option => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => togglePill('allergies', option)}
                          style={{
                            flex: '1 1 calc(50% - 4px)',
                            padding: '8px 16px',
                            borderRadius: '9999px',
                            border: tempHealthInfo.allergies.includes(option) ? '2px solid #2563eb' : '2px solid #e2e8f0',
                            backgroundColor: tempHealthInfo.allergies.includes(option) ? '#2563eb' : '#ffffff',
                            color: tempHealthInfo.allergies.includes(option) ? '#ffffff' : '#475569',
                            fontSize: '13.5px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.18s ease',
                            minWidth: 'fit-content'
                          }}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                    {tempHealthInfo.allergies.includes('Others (Please Specify)') && (
                      <InputWithCounter
                        value={tempHealthInfo.allergyOther}
                        onChange={(val) => setTempHealthInfo({ ...tempHealthInfo, allergyOther: val })}
                        placeholder="Please specify"
                        required={tempHealthInfo.allergies.includes('Others (Please Specify)')}
                      />
                    )}
                    {userSex !== "male" && tempHealthInfo.allergies.includes('Hormonal Medications (Pills, Patches, Injections)') && (
                      <TextareaWithCounter
                        value={tempHealthInfo.hormonalAllergyDetails}
                        onChange={(val) => setTempHealthInfo({ ...tempHealthInfo, hormonalAllergyDetails: val })}
                        placeholder="Please specify details about your hormonal medication allergy"
                        required={tempHealthInfo.allergies.includes('Hormonal Medications (Pills, Patches, Injections)')}
                      />
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>Lifestyle Indicators</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {LIFESTYLE_OPTIONS.map(item => (
                        <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '12px', alignItems: 'center' }}>
                          <label style={{ fontSize: '13.5px', fontWeight: 500, color: '#64748b' }}>{item.label}</label>
                          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            {item.options.map(option => (
                              <label key={option} style={{ flex: '1 1 calc(33.33% - 8px)', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '9px 14px', border: (item.id === 'smoking' ? tempHealthInfo.smoking === option : tempHealthInfo.alcoholUse === option) ? '2px solid #3b82f6' : '2px solid #e2e8f0', borderRadius: '12px', backgroundColor: (item.id === 'smoking' ? tempHealthInfo.smoking === option : tempHealthInfo.alcoholUse === option) ? '#eff6ff' : '#ffffff', minWidth: 'fit-content' }}>
                                <input
                                  type="radio"
                                  name={item.id}
                                  value={option}
                                  checked={item.id === 'smoking' ? tempHealthInfo.smoking === option : tempHealthInfo.alcoholUse === option}
                                  onChange={e => {
                                    if (item.id === 'smoking') {
                                      setTempHealthInfo({ ...tempHealthInfo, smoking: e.target.value });
                                    } else {
                                      setTempHealthInfo({ ...tempHealthInfo, alcoholUse: e.target.value });
                                    }
                                  }}
                                  style={{ width: '18px', height: '18px', accentColor: '#2563eb' }}
                                />
                                <span style={{ fontSize: '13.5px', color: '#334155', fontWeight: 500 }}>{option}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Sexual Health History */}
              {healthInfoStep === 1 && (
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 18px 0' }}>Sexual Health History</h3>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>History of Sexually Transmitted Infections</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                      {STI_HISTORY_OPTIONS.map(option => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => togglePill('stiHistory', option)}
                          style={{
                            flex: '1 1 calc(50% - 4px)',
                            padding: '8px 16px',
                            borderRadius: '9999px',
                            border: tempHealthInfo.stiHistory.includes(option) ? '2px solid #2563eb' : '2px solid #e2e8f0',
                            backgroundColor: tempHealthInfo.stiHistory.includes(option) ? '#2563eb' : '#ffffff',
                            color: tempHealthInfo.stiHistory.includes(option) ? '#ffffff' : '#475569',
                            fontSize: '13.5px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.18s ease',
                            minWidth: 'fit-content'
                          }}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                    {tempHealthInfo.stiHistory.includes('Others (Please Specify)') && (
                      <InputWithCounter
                        value={tempHealthInfo.stiOther}
                        onChange={(val) => setTempHealthInfo({ ...tempHealthInfo, stiOther: val })}
                        placeholder="Please specify"
                        required={tempHealthInfo.stiHistory.includes('Others (Please Specify)')}
                      />
                    )}
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>HIV Status</label>
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
                      {['Negative', 'Positive', 'Unknown'].map(option => (
                        <label key={option} style={{ flex: '1 1 calc(33.33% - 8px)', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '9px 14px', border: tempHealthInfo.hivStatus === option ? '2px solid #3b82f6' : '2px solid #e2e8f0', borderRadius: '12px', backgroundColor: tempHealthInfo.hivStatus === option ? '#eff6ff' : '#ffffff', minWidth: 'fit-content' }}>
                          <input
                            type="radio"
                            name="hivStatus"
                            value={option}
                            checked={tempHealthInfo.hivStatus === option}
                            onChange={e => setTempHealthInfo({ ...tempHealthInfo, hivStatus: e.target.value })}
                            style={{ width: '18px', height: '18px', accentColor: '#2563eb' }}
                          />
                          <span style={{ fontSize: '13.5px', color: '#334155', fontWeight: 500 }}>{option}</span>
                        </label>
                      ))}
                    </div>
                    {(tempHealthInfo.hivStatus === 'Negative' || tempHealthInfo.hivStatus === 'Positive') && (
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#64748b', marginBottom: '8px' }}>Last Date Tested</label>
                        <input
                          type="date"
                          value={tempHealthInfo.lastTestedDate}
                          onChange={e => setTempHealthInfo({ ...tempHealthInfo, lastTestedDate: e.target.value })}
                          min={patientDetails.dob}
                          max={new Date().toISOString().split('T')[0]}
                          style={{
                            width: '100%',
                            padding: '11px 14px',
                            borderRadius: '12px',
                            border: '2px solid #e2e8f0',
                            fontSize: '14.5px',
                            outline: 'none',
                            transition: 'border-color 0.18s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>History of Genital Infections / Other Conditions</label>
                    <TextareaWithCounter
                      value={tempHealthInfo.genitalInfections}
                      onChange={(val) => setTempHealthInfo({ ...tempHealthInfo, genitalInfections: val })}
                      placeholder="Enter details here..."
                    />
                  </div>
                </div>
              )}

              {/* Step 3: Medication History */}
              {healthInfoStep === 2 && (
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 18px 0' }}>Medication History</h3>

                  {userSex !== "male" && (
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>Currently on Hormonal Contraception</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                        {HORMONAL_CONTRACEPTION_OPTIONS.map(option => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => togglePill('hormonalContraception', option)}
                            style={{
                              flex: '1 1 calc(50% - 4px)',
                              padding: '8px 16px',
                              borderRadius: '9999px',
                              border: tempHealthInfo.hormonalContraception.includes(option) ? '2px solid #2563eb' : '2px solid #e2e8f0',
                              backgroundColor: tempHealthInfo.hormonalContraception.includes(option) ? '#2563eb' : '#ffffff',
                              color: tempHealthInfo.hormonalContraception.includes(option) ? '#ffffff' : '#475569',
                              fontSize: '13.5px',
                              fontWeight: 500,
                              cursor: 'pointer',
                              transition: 'all 0.18s ease',
                              minWidth: 'fit-content'
                            }}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                      {tempHealthInfo.hormonalContraception.includes('Others (Please Specify)') && (
                        <InputWithCounter
                          value={tempHealthInfo.hormonalContraceptionOther}
                          onChange={(val) => setTempHealthInfo({ ...tempHealthInfo, hormonalContraceptionOther: val })}
                          placeholder="Please specify"
                          required={tempHealthInfo.hormonalContraception.includes('Others (Please Specify)')}
                        />
                      )}
                    </div>
                  )}

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>Past Surgeries</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                      {(() => {
                        let surgeryOptions = ["Abdominal Section", "Pelvic Surgery"];
                        if (userSex === "female") {
                          surgeryOptions.push("Cesarean Section", "Hysterectomy");
                        } else if (userSex === "male") {
                          surgeryOptions.push("Prostate Surgery", "Testicular Surgery");
                        }
                        surgeryOptions.push("Others (Please Specify)");
                        return surgeryOptions;
                      })().map(option => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => togglePill('pastSurgeries', option)}
                          style={{
                            flex: '1 1 calc(50% - 4px)',
                            padding: '8px 16px',
                            borderRadius: '9999px',
                            border: tempHealthInfo.pastSurgeries.includes(option) ? '2px solid #2563eb' : '2px solid #e2e8f0',
                            backgroundColor: tempHealthInfo.pastSurgeries.includes(option) ? '#2563eb' : '#ffffff',
                            color: tempHealthInfo.pastSurgeries.includes(option) ? '#ffffff' : '#475569',
                            fontSize: '13.5px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.18s ease',
                            minWidth: 'fit-content'
                          }}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                    {tempHealthInfo.pastSurgeries.includes('Others (Please Specify)') && (
                      <InputWithCounter
                        value={tempHealthInfo.pastSurgeryOther}
                        onChange={(val) => setTempHealthInfo({ ...tempHealthInfo, pastSurgeryOther: val })}
                        placeholder="Please specify"
                        required={tempHealthInfo.pastSurgeries.includes('Others (Please Specify)')}
                      />
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>Current Medications</label>
                    <TextareaWithCounter
                      value={tempHealthInfo.currentMedications}
                      onChange={(val) => setTempHealthInfo({ ...tempHealthInfo, currentMedications: val })}
                      placeholder="Enter current medications here..."
                    />
                  </div>
                </div>
              )}

              {/* Step 4: Emergency Contact */}
              {healthInfoStep === 3 && (
                <div>
                  <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#0f172a', margin: '0 0 14px 0' }}>Emergency Contact</h3>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                    <div style={{ gridColumn: 'span 2' }}>
                      <label style={{ display: 'block', fontSize: '13.5px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>Name</label>
                      <input
                        type="text"
                        value={tempHealthInfo.emergencyContactName}
                        onChange={e => {
                          const sanitized = e.target.value.replace(/[^a-zA-Z'\- ]/g, '');
                          setTempHealthInfo({ ...tempHealthInfo, emergencyContactName: sanitized.slice(0, 50) });
                        }}
                        placeholder="Enter emergency contact name"
                        maxLength={50}
                        style={{
                          width: '100%',
                          padding: '10px 13px',
                          borderRadius: '10px',
                          border: '2px solid #e2e8f0',
                          fontSize: '14px',
                          outline: 'none',
                          transition: 'border-color 0.18s ease',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13.5px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>Relationship</label>
                      <select
                        value={tempHealthInfo.emergencyContactRelationship}
                        onChange={e => setTempHealthInfo({ ...tempHealthInfo, emergencyContactRelationship: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 13px',
                          borderRadius: '10px',
                          border: '2px solid #e2e8f0',
                          fontSize: '14px',
                          outline: 'none',
                          transition: 'border-color 0.18s ease',
                          boxSizing: 'border-box',
                          backgroundColor: 'white',
                          cursor: 'pointer'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                      >
                        <option value="">Select relationship</option>
                        <option value="Parent">Parent</option>
                        <option value="Partner">Partner</option>
                        <option value="Guardian">Guardian</option>
                        <option value="Sibling">Sibling</option>
                        <option value="Relative">Relative</option>
                        <option value="Other">Other</option>
                      </select>
                      {tempHealthInfo.emergencyContactRelationship === 'Other' && (
                        <div style={{ marginTop: '8px' }}>
                          <input
                            type="text"
                            value={tempHealthInfo.emergencyContactRelationshipOther}
                            onChange={e => {
                              const sanitized = e.target.value.replace(/[^a-zA-Z]/g, '');
                              setTempHealthInfo({ ...tempHealthInfo, emergencyContactRelationshipOther: sanitized.slice(0, 20) });
                            }}
                            placeholder="Specify relationship"
                            maxLength={20}
                            style={{
                              width: '100%',
                              padding: '10px 13px',
                              borderRadius: '10px',
                              border: '2px solid #e2e8f0',
                              fontSize: '14px',
                              outline: 'none',
                              transition: 'border-color 0.18s ease',
                              boxSizing: 'border-box'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13.5px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>Contact Number</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>+63</span>
                        <input
                          type="tel"
                          value={tempHealthInfo.emergencyContactNumber}
                          onChange={e => {
                            const sanitized = e.target.value.replace(/[^0-9]/g, '');
                            setTempHealthInfo({ ...tempHealthInfo, emergencyContactNumber: sanitized.slice(0, 10) });
                            if (contactNumberError) {
                              setContactNumberError("");
                            }
                          }}
                          placeholder="9XXXXXXXXX"
                          maxLength={10}
                          style={{
                            flex: 1,
                            padding: '10px 13px',
                            borderRadius: '10px',
                            border: `2px solid ${contactNumberError ? '#dc2626' : '#e2e8f0'}`,
                            fontSize: '14px',
                            outline: 'none',
                            transition: 'border-color 0.18s ease',
                            boxSizing: 'border-box'
                          }}
                          onFocus={(e) => e.target.style.borderColor = contactNumberError ? '#dc2626' : '#3b82f6'}
                          onBlur={(e) => e.target.style.borderColor = contactNumberError ? '#dc2626' : '#e2e8f0'}
                        />
                      </div>
                      {contactNumberError && (
                        <p style={{ 
                          color: '#dc2626', 
                          fontSize: '12px', 
                          margin: '4px 0 0 0' 
                        }}>
                          {contactNumberError}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ 
              padding: '14px 24px 18px 24px',
              background: '#f8fafc',
              borderTop: '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  type="button" 
                  onClick={() => {
                    if (healthInfoStep > 0) {
                      setHealthInfoStep(prev => prev - 1);
                    } else {
                      setShowHealthModal(false);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    borderRadius: '12px',
                    border: '2px solid #e2e8f0',
                    background: '#ffffff',
                    color: '#475569',
                    fontSize: '14.5px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.18s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                    e.currentTarget.style.borderColor = '#cbd5e1';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#ffffff';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }}
                >
                  {healthInfoStep > 0 ? 'Previous' : 'Cancel'}
                </button>
                {healthInfoStep < HEALTH_INFO_STEPS.length - 1 ? (
                  <button 
                    type="button" 
                    onClick={() => {
                      if (validateCurrentStep()) {
                        setHealthInfoStep(prev => prev + 1);
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                      color: '#ffffff',
                      fontSize: '14.5px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.18s ease',
                      boxShadow: '0 4px 10px rgba(37, 99, 235, 0.28)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 6px 14px rgba(37, 99, 235, 0.32)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 10px rgba(37, 99, 235, 0.28)';
                    }}
                  >
                    Next
                  </button>
                ) : (
                  <button 
                    type="button" 
                    onClick={handleSaveHealthInfo}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                      color: '#ffffff',
                      fontSize: '14.5px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.18s ease',
                      boxShadow: '0 4px 10px rgba(22, 163, 74, 0.28)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 6px 14px rgba(22, 163, 74, 0.32)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 10px rgba(22, 163, 74, 0.28)';
                    }}
                  >
                    Save
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <CancelAppointmentModal 
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setApptToCancel(null);
        }}
        onConfirm={handleConfirmCancel}
        appointment={apptToCancel}
        theme="user"
        isSubmitting={isCancelling}
      />
    </section>
  );
}
